import React, { useState, useRef, useEffect, type JSX } from "react";
import { Link, useLocation } from "react-router-dom";
 
 import { Alert } from "./Alert";
 import { InvitationService, type Invitation } from "../services/invitationService";
 import { LabService, type Lab } from "../services/labService";
 import { validateEmail } from "../utils/validation";
 import { useAuth } from "../contexts/useAuth";

interface Message {
  text: string;
  type: 'error' | 'warning' | 'success';
}

interface ErrorDetails {
  text: string;
  type: string;
}

interface ValidationErrors {
  [key: string]: string;
}

export const Invitations: React.FC = () => {
  const alertRef = useRef<HTMLDivElement>(null);
  const { user } = useAuth();
  const location = useLocation();
  const adminWarningModalRef = useRef<HTMLDialogElement>(null);
 
  const queryParams = new URLSearchParams(location.search);
  const initialLabId = queryParams.get('labId') || '';
  const initialType = queryParams.get('type') as 'user' | 'admin' | 'organisation' | null;

  const [message, setMessage] = useState<Message>({ text: '', type: 'error' });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [canSendInvitations, setCanSendInvitations] = useState<boolean>(false);
  const [errorDetails, setErrorDetails] = useState<ErrorDetails | null>(null);
  const [emailsInput, setEmailsInput] = useState<string>('');
  const [sendingInvitations, setSendingInvitations] = useState<boolean>(false);
  const [inviteType, setInviteType] = useState<'user' | 'admin' | 'organisation'>(initialType || 'user');
  const [pendingAdminEmails, setPendingAdminEmails] = useState<string[]>([]);
  const [selectedLabId, setSelectedLabId] = useState<string>(initialLabId);
  const [availableLabs, setAvailableLabs] = useState<Lab[]>([]);
  const [isLabsLoading, setIsLabsLoading] = useState<boolean>(false);

  useEffect(() => {
    const isAdmin = user?.is_organisation_admin || false;
    const isChampion = user?.current_lab && user?.can_invite; // Basic check for champion/permitted
    setCanSendInvitations(user ? (user.can_invite || false) : false);
    
    if (!isAdmin && isChampion && user?.current_lab) {
      setSelectedLabId(user.current_lab);
      setInviteType('user');
    }
    
    fetchInvitations();
    if (isAdmin) {
      fetchLabs();
    }
  }, [user]);

  const fetchLabs = async (): Promise<void> => {
    setIsLabsLoading(true);
    try {
      const result = await LabService.getOrganisationLabsWithStats();
      setAvailableLabs(result.lab_stats || []);
    } catch (err) {
      console.error("Failed to fetch labs:", err);
    } finally {
      setIsLabsLoading(false);
    }
  };

  const fetchInvitations = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const result = await InvitationService.getMyInvitations();
      setInvitations(result.data || []);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to load invitations.';
      setMessage({ text: message, type: 'error' });
      setInvitations([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLTextAreaElement>): void => {
    setEmailsInput(e.target.value);
  };

  const parseEmails = (input: string): string[] => {
    // Split by commas and/or newlines
    return input
      .split(/[\n,]/)
      .map(email => email.trim())
      // Filter out empty strings
      .filter(email => email);
  };

  const validateEmails = (): { errors: ValidationErrors; hasError: boolean } => {
    const errors: ValidationErrors = {};
    let hasError: boolean = false;

    const emailList = parseEmails(emailsInput);
    emailList.forEach((email, index) => {
      const emailError = validateEmail(email);
      if (emailError) {
        errors[`email${index}`] = emailError;
        hasError = true;
      }
    });

    if (emailList.length === 0) {
      errors['general'] = 'At least one email address is required';
      hasError = true;
    }

    return { errors, hasError };
  };

  const handleSendInvitations = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setSendingInvitations(true);
    setMessage({ text: '', type: 'error' });
    setErrorDetails(null);

    const { errors, hasError } = validateEmails();

    if (hasError) {
      setMessage({ text: errors['general'] || 'Please check if all the email addresses are correct', type: 'error' });
      setSendingInvitations(false);
      return;
    }

    const validEmails = parseEmails(emailsInput);

    // If inviting as admin, show warning modal first
    if (inviteType === 'admin') {
      setPendingAdminEmails(validEmails);
      adminWarningModalRef.current?.showModal();
      setSendingInvitations(false);
      return;
    }
    // Organisation invite
    if (inviteType === 'organisation') {
      await sendInvitationsOfType(validEmails, 'organisation');
      return;
    }
    // Otherwise send user invitations directly
    await sendInvitationsOfType(validEmails, 'user');
  };

  const confirmAdminInvite = async (): Promise<void> => {
    adminWarningModalRef.current?.close();
    await sendInvitationsOfType(pendingAdminEmails, 'admin');
    setPendingAdminEmails([]);
  };

  const sendInvitationsOfType = async (emails: string[], type: 'user' | 'admin' | 'organisation'): Promise<void> => {
    setSendingInvitations(true);
    setMessage({ text: '', type: 'error' });
    setErrorDetails(null);

    try {
      let result: any = undefined;
      const labIdToPass = selectedLabId || undefined;
      
      if (type === 'admin') {
        result = await InvitationService.sendAdminInvitations(emails, labIdToPass);
      } else if (type === 'user') {
        result = await InvitationService.sendUserInvitations(emails, labIdToPass);
      } else if (type === 'organisation') {
        result = await InvitationService.sendInvitations(emails);
      }

      result = result || {};
      const results = result.results || [];

      if (Array.isArray(results)) {
        const successCount = results.filter(r => r.status === 'sent' || r.status === 'exists').length;
        const errorCount = results.filter(r => r.status === 'error').length;

        if (errorCount === 0) {
          setMessage({
            text: `Successfully sent ${successCount} ${type} invitation${successCount !== 1 ? 's' : ''}`,
            type: 'success'
          });
          setEmailsInput('');
        } else {
          const errorDetailsList = results
            .filter(r => r.status === 'error')
            .map(r => `${r.email}: ${r.message || 'Error'}`);

          setMessage({
            text: `Sent ${successCount} invitations with ${errorCount} errors`,
            type: 'warning'
          });

          setErrorDetails({
            text: errorDetailsList.join('\n') + (errorDetailsList.length > 0 ? '\n' : ''),
            type: 'error'
          });
        }
      } else {
        setMessage({ text: result.message || 'Invitations sent.', type: 'success' });
      }
    } catch (err: unknown) {
      if (
        err &&
        typeof err === 'object' &&
        'invalid_emails' in err &&
        Array.isArray((err as { invalid_emails: string[] }).invalid_emails)
      ) {
        setErrorDetails({
          text: `Invalid emails: ${(err as { invalid_emails: string[] }).invalid_emails.join(', ')}`,
          type: 'error'
        });
      }
      const errorMessage =
        err instanceof Error
          ? err.message
          : err && typeof err === 'object' && 'error' in err && typeof (err as { error: string }).error === 'string'
            ? (err as { error: string }).error
            : 'Failed to send invitations';
      setMessage({ text: errorMessage, type: 'error' });
    } finally {
      await fetchInvitations();
      setSendingInvitations(false);
    }
  };
  const handleResendInvitation = async (email: string): Promise<void> => {
    try {
      const result = await InvitationService.resendInvitation(email);

      setMessage({ text: result.message, type: 'success' });
      await fetchInvitations();
    } catch (err: unknown) {
      const message = err && typeof err === 'object' && 'error' in err && typeof (err as { error: string }).error === 'string'
        ? (err as { error: string }).error
        : 'Failed to resend invitation';
      setMessage({ text: message, type: 'error' });
    }
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  const getStatusBadge = (invitation: Invitation): JSX.Element => {
    if (invitation.is_used) {
      return <div className="badge badge-success">Used</div>;
    } else if (invitation.is_expired) {
      return <div className="badge badge-error">Expired</div>;
    } else if (invitation.is_valid) {
      return <div className="badge badge-primary">Valid</div>;
    } else {
      return <div className="badge badge-warning">Invalid</div>;
    }
  };

  // Only restrict the panel if the user is not an admin at all
  // Allow access if admin OR champion OR has invite permission
  const hasAccess = user && (user.is_organisation_admin || user.can_invite);

  if (!hasAccess) {
    return (
      <div className="min-h-screen p-4">
        <div className="max-w-6xl mx-auto">
          <div className="bg-amber-50 rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h1 className="text-3xl font-bold">Invitations</h1>
              <Link to="/dashboard" className="btn btn-neutral btn-sm">Back to Dashboard</Link>
            </div>
            <Alert type="error" message="You don't have permission to view this page. Contact your administrator if you believe this is an error." />
          </div>
        </div>
      </div>
    );
  }
 
  const isAdmin = user?.is_organisation_admin;
  const currentLabName = availableLabs.find(l => l.id === selectedLabId)?.name || "your lab";

  return (
    <div className="min-h-screen p-4">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-amber-50 rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold">Invitations</h1>
              <p className="text-base-content/70">
                Send invitation codes for LAB ZER0 registration
              </p>
            </div>
            <Link to="/dashboard" className="btn btn-neutral btn-sm">Back to Dashboard</Link>
          </div>
        </div>

        {/* Alert Section */}
        <div ref={alertRef} className="space-y-2">
          {message.text && (
            <Alert type={message.type} message={message.text} />
          )}
          {errorDetails && (
            <div className="ml-8">
              <Alert type="error" message={errorDetails.text} />
            </div>
          )}
        </div>

        {/* Send Invitations Form */}
        <div className="bg-amber-50 rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">Send New Invitations</h2>

          {/* Admin Warning Modal */}
          <dialog ref={adminWarningModalRef} className="modal">
            <div className="modal-box">
              <h3 className="font-bold text-lg text-warning">Grant admin access?</h3>
              <p className="py-4">
                This person will be able to access the Admin Dashboard and manage
                organization settings. Only grant access to trusted individuals.
              </p>
              <div className="modal-action">
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    adminWarningModalRef.current?.close();
                    setPendingAdminEmails([]);
                    setSendingInvitations(false);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={confirmAdminInvite}
                >
                  Invite Admin
                </button>
              </div>
            </div>
          </dialog>

          <form onSubmit={handleSendInvitations} className="space-y-4">
            {/* Invitation Type Selector */}
            <fieldset className="fieldset">
              <legend className="fieldset-legend">Invite as</legend>
              <div className="flex gap-6 mt-2">
                <label className="cursor-pointer flex items-center gap-2">
                  <input
                    type="radio"
                    name="inviteType"
                    value="user"
                    checked={inviteType === 'user'}
                    onChange={() => setInviteType('user')}
                    className="radio radio-primary"
                  />
                  <span className="label-text">User</span>
                </label>
                <label className={`cursor-pointer flex items-center gap-2 ${isAdmin ? '' : 'hidden'}`}>
                  <input
                    type="radio"
                    name="inviteType"
                    value="admin"
                    checked={inviteType === 'admin'}
                    onChange={() => setInviteType('admin')}
                    className="radio radio-primary"
                  />
                  <span className="label-text">Admin</span>
                </label>
                {/* Organisation invite button, only enabled if canSendInvitations is true */}
                <label className={`cursor-pointer flex items-center gap-2 ${isAdmin && canSendInvitations ? '' : 'hidden'}`}>
                  <input
                    type="radio"
                    name="inviteType"
                    value="organisation"
                    checked={inviteType === 'organisation'}
                    onChange={() => setInviteType('organisation')}
                    className="radio radio-primary"
                  />
                  <span className="label-text">Organisation</span>
                </label>
              </div>
              {inviteType === 'admin' && (
                <p className="text-sm text-warning mt-2">
                  ⚠️ Admins will have full access to organization settings
                </p>
              )}
              {inviteType === 'organisation' && (
                <p className="text-sm text-info mt-2">
                  Organisation invites allow an entire organisation to register.
                </p>
              )}
            </fieldset>

            {/* Lab Selector */}
            {inviteType !== 'organisation' && (
              <fieldset className="fieldset">
                <legend className="fieldset-legend">Target Lab</legend>
                {isAdmin ? (
                  <select 
                    className="select select-bordered w-full"
                    value={selectedLabId}
                    onChange={(e) => setSelectedLabId(e.target.value)}
                    disabled={isLabsLoading}
                  >
                    <option value="">No specific lab (User can choose after registration)</option>
                    {availableLabs.map(lab => (
                      <option key={lab.id} value={lab.id}>{lab.name}</option>
                    ))}
                  </select>
                ) : (
                  <div className="p-3 bg-base-200 rounded text-sm">
                    Inviting to: <strong>{currentLabName}</strong>
                  </div>
                )}
                <p className="label">Users will be automatically added to this lab upon registration</p>
              </fieldset>
            )}

            <fieldset className="fieldset">
              <legend className="fieldset-legend">Email Addresses</legend>
              <textarea
                className="w-full textarea h-48"
                placeholder="admin@university.edu, admin2@university.edu"
                value={emailsInput}
                onChange={handleEmailChange}
              />
              <p className="label">Enter email addresses separated by commas or new lines</p>
            </fieldset>

            <div className="flex gap-2">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={sendingInvitations}
              >
                {sendingInvitations ? (
                  <>
                    <span className="loading loading-spinner loading-sm"></span>
                    Sending Invitations...
                  </>
                ) : (
                  inviteType === 'organisation'
                    ? 'Send Organisation Invitations'
                    : `Send ${inviteType === 'admin' ? 'Admin' : 'User'} Invitations`
                )}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setEmailsInput('')}>
                Clear
              </button>
            </div>
          </form>
        </div>

        {/* Invitations List */}
        <div className="bg-amber-50 rounded-lg shadow-md p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Sent Invitations</h2>
            <button
              className="btn btn-neutral btn-sm"
              onClick={fetchInvitations}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="loading loading-spinner loading-sm"></span>
              ) : (
                'Refresh'
              )}
            </button>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="flex flex-col items-center gap-4">
                <span className="loading loading-spinner loading-lg"></span>
                <p>Loading invitations...</p>
              </div>
            </div>
          ) : invitations.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-base-content/70">No invitations sent yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="table table-zebra w-full">
                <thead>
                  <tr>
                    <th>Email</th>
                    <th>Role</th>
                    <th>Code</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Expires</th>
                    <th>Used</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invitations.map((invitation) => (
                    <tr key={invitation.code}>
                      <td className="font-medium">{invitation.email}</td>
                      <td className="text-sm">{invitation.role || '-'}</td>
                      <td>
                        <code className="bg-base-200 px-2 py-1 rounded text-sm">
                          {invitation.code}
                        </code>
                      </td>
                      <td>{getStatusBadge(invitation)}</td>
                      <td className="text-sm">{formatDate(invitation.created_at)}</td>
                      <td className="text-sm">{formatDate(invitation.expires_at)}</td>
                      <td className="text-sm">
                        {invitation.used_at ? formatDate(invitation.used_at) : '-'}
                      </td>
                      <td>
                        {invitation.is_valid && !invitation.is_used && (
                          <button
                            className="btn btn-outline btn-sm"
                            onClick={() => handleResendInvitation(invitation.email)}
                          >
                            Resend
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
